import { useState, useEffect, useMemo } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t, pickLang } from "@/lib/i18n";
import { COUNTRY_CATALOG } from "@/lib/countries";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, GraduationCap, Rocket, ArrowRight, Building2, PlayCircle, Bookmark, CheckCircle2, Trash2, Users, Download, Bot } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { ApplyDialog } from "@/components/CompanyBits";
import { http as _http } from "@/lib/api";
import CoachChat from "@/components/CoachChat";

export default function Roadmap() {
  const { user, lang } = useAuth();
  const nav = useNavigate();
  const [career, setCareer] = useState("");
  const [level, setLevel] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [result, setResult] = useState(null);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyCo, setApplyCo] = useState(null);
  const [savedRoadmaps, setSavedRoadmaps] = useState([]);
  const [currentSaveId, setCurrentSaveId] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [coachOpen, setCoachOpen] = useState(false);

  const availableRegions = useMemo(() => {
    const c = COUNTRY_CATALOG.find((x) => x.name === country);
    return c ? c.regions : [];
  }, [country]);

  const loadSaved = () => user && http.get("/roadmaps/mine").then((r) => setSavedRoadmaps(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, [user]);

  // Prefill country/region from the user's saved profile so the AI adapts to
  // the local education system without asking each time.
  useEffect(() => {
    if (!user) return;
    if (!country && user.home_country) setCountry(user.home_country);
    if (!region && user.home_region) setRegion(user.home_region);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const go = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!career.trim()) { toast.error(lang === "fr" ? "Renseignez un métier" : "Enter a career"); return; }
    setLoading(true);
    setCurrentSaveId(null);
    try {
      const { data } = await http.post("/ai/roadmap", {
        target_career: career,
        current_level: level || null,
        country: country || null,
        region: region || null,
        language: lang,
      });
      setResult(data);
      // Fetch video (best-effort)
      try {
        const v = await http.get("/careers/video", { params: { q: career } });
        setVideo(v.data);
      } catch { setVideo(null); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI error");
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!result) return;
    try {
      const { data } = await http.post("/roadmaps/save", {
        target_career: career,
        current_level: level,
        country: country || null,
        region: region || null,
        data: result,
      });
      setCurrentSaveId(data.id);
      toast.success(lang === "fr" ? "Roadmap sauvegardée" : "Roadmap saved");
      loadSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openSaved = (rm) => {
    setCareer(rm.target_career);
    setLevel(rm.current_level || "");
    setResult(rm.data);
    setCurrentSaveId(rm.id);
    http.get("/careers/video", { params: { q: rm.target_career } }).then((r) => setVideo(r.data)).catch(() => {});
  };

  const toggleStep = async (idx) => {
    if (!currentSaveId) { toast.error(lang === "fr" ? "Sauvegardez d'abord la roadmap" : "Save the roadmap first"); return; }
    const current = savedRoadmaps.find((r) => r.id === currentSaveId);
    const existing = current?.progress?.find((p) => p.step_index === idx);
    await http.put(`/roadmaps/${currentSaveId}/progress`, { step_index: idx, completed: !existing?.completed });
    loadSaved();
  };

  const currentProgress = savedRoadmaps.find((r) => r.id === currentSaveId)?.progress || [];
  const stepDone = (i) => currentProgress.find((p) => p.step_index === i)?.completed;

  const delRoadmap = async (id) => {
    await http.delete(`/roadmaps/${id}`);
    if (currentSaveId === id) { setCurrentSaveId(null); }
    loadSaved();
  };

  const joinClass = async () => {
    if (!joinCode.trim()) return;
    try {
      const { data } = await http.post("/classes/join", { join_code: joinCode });
      toast.success((lang === "fr" ? "Classe rejointe : " : "Joined class: ") + (data.class_name || ""));
      setJoinCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadPdf = async (rmId, career) => {
    try {
      const resp = await http.get(`/roadmaps/${rmId}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `proxijob_roadmap_${(career || "metier").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Export PDF impossible" : "PDF export failed"));
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-gradient-to-br from-[#F97316] to-[#EA580C] text-white p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div className="relative max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="ai-shimmer w-8 h-8 rounded-full flex items-center justify-center">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-white/90">Proxijob · Youth</span>
            </div>
            <h1 data-testid="roadmap-title" className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t(lang, "roadmap_title")}</h1>
            <p className="mt-3 text-white/90 leading-relaxed">{t(lang, "roadmap_sub")}</p>
            <form onSubmit={go} className="mt-6 grid gap-3">
              <Input data-testid="roadmap-career" value={career} onChange={(e) => setCareer(e.target.value)} placeholder={t(lang, "roadmap_placeholder")} className="h-12 rounded-full bg-white text-slate-900 px-5 border-none" />
              <Input data-testid="roadmap-level" value={level} onChange={(e) => setLevel(e.target.value)} placeholder={t(lang, "roadmap_level_placeholder")} className="h-11 rounded-full bg-white/95 text-slate-900 px-5 border-none" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={country} onValueChange={(v) => { setCountry(v === "__none__" ? "" : v); setRegion(""); }}>
                  <SelectTrigger data-testid="roadmap-country" className="h-11 rounded-full bg-white/95 text-slate-900 px-5 border-none">
                    <SelectValue placeholder={pickLang(lang, { fr: "Pays", en: "Country", es: "País", de: "Land" })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      {pickLang(lang, { fr: "Non précisé", en: "Not specified", es: "Sin especificar", de: "Nicht angegeben" })}
                    </SelectItem>
                    {COUNTRY_CATALOG.map((c) => (
                      <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={region} onValueChange={(v) => setRegion(v === "__none__" ? "" : v)} disabled={!country || availableRegions.length === 0}>
                  <SelectTrigger data-testid="roadmap-region" className="h-11 rounded-full bg-white/95 text-slate-900 px-5 border-none disabled:opacity-60">
                    <SelectValue placeholder={pickLang(lang, { fr: "Région / Province / État", en: "Region / Province / State", es: "Región / Provincia", de: "Region / Bundesland" })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      {pickLang(lang, { fr: "Toutes", en: "Any", es: "Cualquiera", de: "Beliebig" })}
                    </SelectItem>
                    {availableRegions.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-white/70 -mt-1 leading-snug">
                {pickLang(lang, {
                  fr: "Précisez pays + région : chaque province ou état a son propre système éducatif (DEP au Québec, Bac Pro en France, Ausbildung en Allemagne…) — Proxijob adapte automatiquement les diplômes.",
                  en: "Add country + region: each province/state has its own education system (DEP in Québec, Bac Pro in France, Ausbildung in Germany…) — Proxijob adapts the diploma names.",
                  es: "Indica país + región: cada provincia/estado tiene su sistema educativo (FP en España, TSU en México…) — Proxijob adapta los títulos.",
                  de: "Land + Region angeben: jede Region hat ihr Bildungssystem (Ausbildung DE, Matura AT, CFC CH…) — Proxijob passt die Abschlüsse an.",
                })}
              </p>
              <Button data-testid="roadmap-go" type="submit" disabled={loading} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full h-12 w-fit px-6">
                {loading ? t(lang, "analyzing") : t(lang, "roadmap_go")}
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>

        {/* Saved roadmaps + join class */}
        {user && (
          <div className="mt-6 grid md:grid-cols-[2fr_1fr] gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Bookmark className="w-4 h-4 text-[#1E3A8A]" />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Mes roadmaps sauvegardées" : "My saved roadmaps"}</p>
              </div>
              {savedRoadmaps.length === 0 && <p className="text-sm text-slate-500">{lang === "fr" ? "Aucune. Sauvegardez votre parcours pour y revenir plus tard." : "None yet."}</p>}
              <div className="flex flex-wrap gap-2">
                {savedRoadmaps.map((rm) => {
                  const done = (rm.progress || []).filter((p) => p.completed).length;
                  const total = (rm.data?.diploma_track || []).length;
                  return (
                    <div key={rm.id} className={`rounded-full border pl-3 pr-1 py-1 flex items-center gap-2 text-sm ${currentSaveId === rm.id ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200"}`}>
                      <button data-testid={`open-saved-${rm.id}`} onClick={() => openSaved(rm)}>{rm.target_career}</button>
                      <span className="text-xs text-slate-500">{done}/{total}</span>
                      <button
                        data-testid={`download-pdf-${rm.id}`}
                        onClick={(e) => { e.stopPropagation(); downloadPdf(rm.id, rm.target_career); }}
                        title={lang === "fr" ? "Télécharger PDF" : "Download PDF"}
                        className="text-slate-400 hover:text-[#1E3A8A] p-1"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => delRoadmap(rm.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-[#F97316]" />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Rejoindre une classe" : "Join a class"}</p>
              </div>
              <div className="flex gap-2">
                <Input data-testid="join-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="XXXXXX" maxLength={6} className="uppercase" />
                <Button data-testid="join-btn" onClick={joinClass} className="rounded-full bg-[#F97316] hover:bg-[#EA580C]">OK</Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">{lang === "fr" ? "Code fourni par votre enseignant" : "Code given by your teacher"}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-10 space-y-8 fade-up">
            {/* Video */}
            {video?.url && (
              <div className="rounded-2xl bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <PlayCircle className="w-4 h-4 text-[#F97316]" />
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Vidéo métier · 30s" : "Career video · 30s"}</p>
                </div>
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe data-testid="career-video" src={video.url} title={video.title} allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
                </div>
                <p className="text-sm mt-2 text-slate-700">{video.title}</p>
              </div>
            )}

            {/* Save button */}
            <div className="flex flex-wrap gap-3">
              <Button data-testid="save-roadmap-btn" onClick={save} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full">
                <Bookmark className="w-4 h-4 mr-1.5" /> {currentSaveId
                  ? pickLang(lang, { fr: "Roadmap sauvegardée ✓", en: "Roadmap saved ✓" })
                  : pickLang(lang, { fr: "Sauvegarder ma roadmap", en: "Save my roadmap" })}
              </Button>
              <Button
                data-testid="open-coach-btn"
                onClick={() => setCoachOpen(true)}
                className="rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#F97316] hover:opacity-90 text-white"
              >
                <Bot className="w-4 h-4 mr-1.5" /> {pickLang(lang, {
                  fr: "Parler à un coach IA (Claude)",
                  en: "Talk to AI coach",
                  es: "Hablar con coach IA",
                  de: "Mit KI-Coach sprechen",
                })}
              </Button>
            </div>

            {/* Health career highlight — when a health sector is in matched_sectors */}
            {(() => {
              const healthy = (result.matched_sectors || []).some(s => /santé|health|salud|gesundheit/i.test(s));
              const hospitals = (result.entry_companies || []).filter(c => /santé|health|salud|gesundheit/i.test(c.sector || ""));
              if (!healthy && hospitals.length === 0) return null;
              return (
                <div data-testid="health-showcase" className="rounded-3xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center text-2xl shrink-0">
                      🏥
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                        {pickLang(lang, { fr: "Focus Santé", en: "Health focus", es: "Foco Salud", de: "Fokus Gesundheit" })}
                      </p>
                      <h3 className="font-display font-bold text-xl text-emerald-900 mt-1">
                        {pickLang(lang, {
                          fr: "Les hôpitaux près de chez vous recrutent des jeunes",
                          en: "Hospitals near you hire young talent",
                          es: "Los hospitales cerca de ti contratan jóvenes",
                          de: "Krankenhäuser in deiner Nähe stellen Jugendliche ein",
                        })}
                      </h3>
                      <p className="text-sm text-emerald-800 mt-2 leading-relaxed">
                        {pickLang(lang, {
                          fr: "Aide-soignant stagiaire, brancardier, agent d'accueil, ASH, agent de sécurité, service civique : l'hôpital public est le plus gros employeur de jeunes en France. Postulez spontanément à ceux qui vous entourent.",
                          en: "Care assistant, hospital porter, receptionist, hospital service worker, civic service: hospitals are huge youth employers.",
                          es: "Auxiliar de enfermería, camillero, recepcionista, mozo de laboratorio, servicio cívico: los hospitales son grandes empleadores de jóvenes.",
                          de: "Pflegehilfe, Krankentransport, Rezeption, FSJ: Krankenhäuser stellen viele junge Menschen ein.",
                        })}
                      </p>
                      {hospitals.length > 0 ? (
                        <div className="mt-4 grid sm:grid-cols-2 gap-3">
                          {hospitals.slice(0, 4).map(h => (
                            <Link
                              key={h.id}
                              to={`/vitrine/${h.id}`}
                              data-testid={`health-co-${h.id}`}
                              className="block rounded-xl bg-white border border-emerald-200 p-3 hover:border-emerald-400 hover:shadow-sm transition-all"
                            >
                              <p className="font-semibold text-sm text-emerald-900 truncate">🏥 {h.name}</p>
                              <p className="text-xs text-emerald-700 truncate">{h.city}, {h.country}</p>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <Link to="/map?sector=Sant%C3%A9" data-testid="health-explore-btn"
                              className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-emerald-800 hover:text-emerald-900">
                          {pickLang(lang, {
                            fr: "Explorer les hôpitaux sur la carte",
                            en: "Explore hospitals on the map",
                            es: "Explorar hospitales en el mapa",
                            de: "Krankenhäuser auf der Karte",
                          })}
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Multiple pathways (per country/province) */}
            {result.pathways?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-4 h-4 text-[#1E3A8A]" />
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "roadmap_pathways")}</p>
                </div>
                <p className="text-xs text-slate-500 mb-4">{t(lang, "roadmap_pathways_hint")}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {result.pathways.map((pw, pi) => (
                    <div key={pi} data-testid={`roadmap-pathway-${pi}`} className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className="font-display font-bold text-[#1E3A8A] leading-tight flex-1 min-w-0">{pw.name}</p>
                        {pw.duration_total && (
                          <Badge variant="secondary" className="rounded-full shrink-0">{pw.duration_total}</Badge>
                        )}
                      </div>
                      {pw.audience && (
                        <p className="text-xs text-slate-500 mt-2">
                          <span className="font-semibold text-[#F97316]">{t(lang, "roadmap_pathway_audience")} :</span> {pw.audience}
                        </p>
                      )}
                      <ol className="mt-3 space-y-2">
                        {(pw.steps || []).map((st, si) => (
                          <li key={si} className="flex gap-2.5 items-start text-sm">
                            <span className="w-5 h-5 rounded-full bg-[#1E3A8A] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                              {st.step || (si + 1)}
                            </span>
                            <div>
                              <span className="font-semibold text-slate-900">{st.title}</span>
                              {st.duration && <span className="text-xs text-slate-500"> · {st.duration}</span>}
                              {st.description && <p className="text-xs text-slate-600 mt-0.5 leading-snug">{st.description}</p>}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Diploma track with progress */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="w-4 h-4 text-[#1E3A8A]" />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "roadmap_diploma_track")}</p>
              </div>
              <div className="relative">
                <div className="hidden md:block absolute left-6 top-0 bottom-0 w-0.5 bg-[#1E3A8A]/20" />
                <div className="space-y-4">
                  {result.diploma_track?.map((step, i) => (
                    <div key={i} data-testid={`roadmap-step-${i}`} className="flex gap-4 items-start">
                      <button
                        onClick={() => toggleStep(i)}
                        data-testid={`step-toggle-${i}`}
                        className={`relative z-10 w-12 h-12 rounded-full font-display font-bold flex items-center justify-center shrink-0 transition-all hover:scale-105 ${
                          stepDone(i) ? "bg-green-600 text-white" : "bg-[#1E3A8A] text-white"
                        }`}
                        title={lang === "fr" ? "Cliquer pour valider l'étape" : "Click to mark complete"}
                      >
                        {stepDone(i) ? <CheckCircle2 className="w-5 h-5" /> : (step.step || (i + 1))}
                      </button>
                      <div className={`rounded-2xl bg-white border p-5 flex-1 ${stepDone(i) ? "border-green-500 bg-green-50/40" : "border-slate-200"}`}>
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                          <h3 className="font-display font-semibold text-lg">{step.title}</h3>
                          <Badge variant="secondary" className="rounded-full">{step.duration}</Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Certifications recommended to boost CV */}
            {result.certifications_optional?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "roadmap_certifications")}</p>
                </div>
                <p className="text-xs text-slate-500 mb-4">{t(lang, "roadmap_certifications_hint")}</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {result.certifications_optional.map((cert, i) => (
                    <div key={i} data-testid={`roadmap-cert-${i}`} className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-5">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                          <GraduationCap className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-display font-semibold text-slate-900 leading-tight">{cert.title}</p>
                          {cert.issuer && <p className="text-xs text-emerald-700 font-medium mt-1">{cert.issuer}</p>}
                          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{cert.why}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Entry-level jobs */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Rocket className="w-4 h-4 text-[#F97316]" />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "roadmap_entry_jobs")}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.entry_level_jobs?.map((j, i) => (
                  <div key={i} data-testid={`entry-job-${i}`} className="rounded-2xl bg-gradient-to-br from-[#FFF7ED] to-white border border-[#F97316]/30 p-5">
                    <p className="font-display font-semibold text-lg">{j.title}</p>
                    <p className="text-xs text-[#C2410C] font-medium mt-1">{j.sector}</p>
                    <p className="text-sm text-slate-700 mt-3 leading-relaxed">{j.why_relevant}</p>
                  </div>
                ))}
              </div>
            </div>

            {result.encouragement && (
              <div className="rounded-3xl bg-[#1E3A8A] text-white p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-[#F97316] font-semibold mb-2">{t(lang, "roadmap_encouragement")}</p>
                <p className="text-lg font-display leading-relaxed">{result.encouragement}</p>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-[#1E3A8A]" />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "roadmap_companies_nearby")}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(result.entry_companies || []).map((c) => (
                  <div key={c.id} data-testid={`roadmap-co-${c.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-md transition-transform">
                    <p className="font-display font-semibold">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.sector} · {c.city}, {c.country}</p>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-3">{c.description}</p>
                    {c.student_tasks?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {c.student_tasks.slice(0, 3).map((s) => <Badge key={s} className="bg-[#FFF7ED] text-[#C2410C] rounded-full text-[10px]">{s}</Badge>)}
                      </div>
                    )}
                    <Button data-testid={`roadmap-apply-${c.id}`} size="sm" onClick={() => setApplyCo(c)} className="mt-4 w-full bg-[#F97316] hover:bg-[#EA580C] rounded-full">
                      {t(lang, "apply_spontaneous")} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                ))}
                {(result.entry_companies || []).length === 0 && (
                  <p className="col-span-full text-sm text-slate-500 text-center py-8">—</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ApplyDialog open={!!applyCo} onOpenChange={(v) => !v && setApplyCo(null)} company={applyCo} youngMode />
      <CoachChat open={coachOpen} onOpenChange={setCoachOpen} targetCareer={result?.target_career || career} />
    </Layout>
  );
}
